import { GeoJSONSource } from "maplibre-gl";
import { useEffect, useMemo } from "react";
import { emptyFeatureCollection } from "../../../util/geo";
import { useCaMap, useWorkspaces } from "../../state";
import { Tag } from "../../state/types";
import { isExploration, workspacesOnMap } from "../../workspaces/kind";
import { tagFillColor, tagHex } from "../../workspaces/tagColors";
import { boundsForList, fitBoundsIfRelevant } from "../util";

export const WorkspaceControl = () => {
  const map = useCaMap();
  const { workspaces, selectedWorkspaceId } = useWorkspaces();

  const selectedWorkspace = useMemo(
    () => workspaces?.find((p) => p.id === selectedWorkspaceId),
    [workspaces, selectedWorkspaceId]
  );

  const data = useMemo(() => {
    if (!workspaces) return emptyFeatureCollection;
    const features = workspacesOnMap(workspaces, selectedWorkspaceId).flatMap((workspace) =>
      (workspace.parcels || []).map((parcel: { tags?: Tag[]; [k: string]: any }) => {
        const firstTag = parcel.tags?.[0];
        const selected = workspace.id === selectedWorkspaceId;
        return {
          type: "Feature",
          geometry: parcel.geometry,
          properties: {
            ...parcel,
            workspaceId: workspace.id,
            workspaceName: workspace.name,
            exploration: isExploration(workspace),
            selected,
            tagFill: firstTag ? tagFillColor(firstTag.color, selected ? 0.55 : 0.25) : null,
            tagOutline: firstTag ? tagHex[firstTag.color] : null,
          },
        };
      })
    );
    return {
      type: "FeatureCollection",
      features,
    };
  }, [workspaces, selectedWorkspaceId]);

  useEffect(() => {
    if (!map) return;
    const s = map.getSource("workspaces") as GeoJSONSource;
    if (s) {
      s.setData(data as any);
    }
  }, [map, data]);

  useEffect(() => {
    if (!(map && selectedWorkspace?.parcels?.length)) return;

    const bounds = boundsForList(selectedWorkspace.parcels);
    fitBoundsIfRelevant(map, bounds);
  }, [map, selectedWorkspace]);

  return null;
};
